import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AtSign, Hash, Search, Trash2, X } from "lucide-react";
import {
  createHashtagOverlay,
  createMentionOverlay,
  type OverlayItem,
  type MentionOverlayPayload,
  type HashtagOverlayPayload,
} from "@scroll/shared-types";

type LinkKind = "mention" | "hashtag";

interface LinkOverlayPickerSheetProps {
  kind: LinkKind | null;
  existing: OverlayItem[];
  onClose: () => void;
  onAdd: (item: OverlayItem) => void;
  onRemove: (id: string) => void;
}

interface UserSearchResult {
  id: string;
  username: string;
  displayName?: string;
  profileImageUrl?: string;
}

interface HashtagSearchResult {
  name: string;
  postCount?: number;
}

export default function LinkOverlayPickerSheet({
  kind,
  existing,
  onClose,
  onAdd,
  onRemove,
}: LinkOverlayPickerSheetProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (kind) {
      setQuery("");
      // Defer focus to next tick so the input is mounted.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [kind]);

  const mentionResults = useQuery<UserSearchResult[]>({
    queryKey: ["/api/users/search", { q: query }],
    queryFn: async () => {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(query)}`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: kind === "mention" && query.length > 0,
    staleTime: 30_000,
  });

  const hashtagResults = useQuery<HashtagSearchResult[]>({
    queryKey: ["/api/hashtags/autocomplete", query],
    queryFn: async () => {
      const res = await fetch(
        `/api/hashtags/autocomplete?q=${encodeURIComponent(query)}`,
        { credentials: "include" },
      );
      if (!res.ok) return [];
      return res.json();
    },
    enabled: kind === "hashtag" && query.length > 0,
    staleTime: 30_000,
  });

  if (!kind) return null;

  const isMention = kind === "mention";
  const title = isMention ? "Add Mention" : "Add Hashtag";
  const placeholder = isMention ? "Search people..." : "Search hashtags...";
  const tagged = existing.filter((o) => o.type === kind);

  const handleSubmitFreeform = () => {
    const trimmed = query.trim().replace(/^[@#]/, "");
    if (!trimmed) return;
    if (isMention) {
      onAdd(createMentionOverlay(trimmed));
    } else {
      onAdd(createHashtagOverlay(trimmed));
    }
    setQuery("");
  };

  return (
    <div
      className="link-picker-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="link-picker-title"
    >
      <div className="link-picker-header">
        <span className="link-picker-title" id="link-picker-title">{title}</span>
        <button
          className="timeline-toolbar-btn"
          onClick={onClose}
          aria-label="Close picker"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="link-picker-search">
        <Search size={14} aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmitFreeform();
            }
          }}
          placeholder={placeholder}
          className="link-picker-input"
          aria-label={placeholder}
        />
      </div>

      <div className="link-picker-results">
        {isMention && mentionResults.data && mentionResults.data.length > 0 && (
          <ul className="link-picker-list" role="list">
            {mentionResults.data.slice(0, 8).map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  className="link-picker-item"
                  onClick={() => {
                    onAdd(createMentionOverlay(user.username, { userId: user.id }));
                    setQuery("");
                  }}
                >
                  <div className="link-picker-avatar">
                    {user.profileImageUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={user.profileImageUrl} alt="" />
                    ) : (
                      <span>{(user.displayName || user.username || "?").charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="link-picker-item-text">
                    <span className="link-picker-item-primary">
                      {user.displayName || user.username}
                    </span>
                    <span className="link-picker-item-secondary">@{user.username}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {!isMention && hashtagResults.data && hashtagResults.data.length > 0 && (
          <ul className="link-picker-list" role="list">
            {hashtagResults.data.slice(0, 8).map((tag) => (
              <li key={tag.name}>
                <button
                  type="button"
                  className="link-picker-item"
                  onClick={() => {
                    onAdd(createHashtagOverlay(tag.name));
                    setQuery("");
                  }}
                >
                  <div className="link-picker-avatar link-picker-avatar-hashtag">
                    <Hash size={16} aria-hidden="true" />
                  </div>
                  <div className="link-picker-item-text">
                    <span className="link-picker-item-primary">#{tag.name}</span>
                    {typeof tag.postCount === "number" && (
                      <span className="link-picker-item-secondary">
                        {tag.postCount.toLocaleString()} posts
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {query.trim().length > 0 && (
          <div className="link-picker-freeform">
            <button
              type="button"
              className="link-picker-freeform-btn"
              onClick={handleSubmitFreeform}
            >
              {isMention ? <AtSign size={14} /> : <Hash size={14} />}
              <span>
                Tag <strong>{query.replace(/^[@#]/, "")}</strong> as-is
              </span>
            </button>
          </div>
        )}
      </div>

      {tagged.length > 0 && (
        <div className="link-picker-tagged">
          <div className="link-picker-tagged-label">Already tagged</div>
          <ul className="link-picker-chip-row" role="list">
            {tagged.map((item) => {
              const label = isMention
                ? `@${(item.payload as MentionOverlayPayload).username}`
                : `#${(item.payload as HashtagOverlayPayload).name}`;
              return (
                <li key={item.id} className="link-picker-chip">
                  <span>{label}</span>
                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    aria-label={`Remove ${label}`}
                  >
                    <Trash2 size={12} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
